import { type ComponentProps, type ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/Avatar/Avatar';
import { useBackTarget } from '@/layouts/backTarget';
import { BackLink } from '@/layouts/BackLink';
import { cn } from '@/util/utils';

type PageHeaderProps = Omit<ComponentProps<'header'>, 'title'> & {
    title: ReactNode;
    /** `main`: 68px, eyebrow over the title. `subpage`: 126px, back arrow, breadcrumb, subtitle, actor chip. */
    variant?: 'main' | 'subpage';
    eyebrow?: ReactNode;
    breadcrumb?: ReactNode;
    subtitle?: ReactNode;
    /**
     * The screen's own default for the back arrow; subpages only. Where the visitor actually came
     * from wins over it, which is the shared rule in `backTarget` and not this screen's business
     * (ALS-105). The default is what a direct link or a bookmark gets.
     */
    backTo?: string;
    /** Who is working here and on what ("Scarlett", "Creating order"); subpages only. */
    actor?: { name: string; state?: string };
    /** The search field or palette, main pages only. */
    search?: ReactNode;
    /** Trailing buttons and badges. */
    actions?: ReactNode;
};

/**
 * app-page-header, READ geometry: main is 68 high on the card surface, subpage 126, both padded
 * 24 across with a hairline under. Below 768 the heights become minimums: the actions stay on
 * the title's row while both fit and wrap under the title when they do not, and the search,
 * when there is one, takes the whole row beneath.
 */
function PageHeader({
    title,
    variant = 'main',
    eyebrow,
    breadcrumb,
    subtitle,
    backTo,
    actor,
    search,
    actions,
    className,
    ...props
}: PageHeaderProps) {
    const subpage = variant === 'subpage';
    const back = useBackTarget(backTo);
    return (
        <header
            data-slot="page-header"
            data-variant={variant}
            className={cn(
                'border-border flex shrink-0 flex-wrap items-center gap-x-6 gap-y-3 border-b px-4 py-3 sm:px-(--layout-body-padding)',
                subpage
                    ? 'min-h-[126px] bg-inherit md:items-start md:py-6'
                    : // 68 tall with no vertical padding, as the prototype's main header is
                      // (layout.css .app-page-header); a phone keeps its padding for the wrap.
                      'bg-card min-h-[68px] md:py-0',
                className,
            )}
            {...props}
        >
            {subpage && back ? (
                // The arrow stays the design's 16px; on touch an invisible 44px target is centred
                // on it, inside the header's padding and the 24px gap, so it overlaps nothing.
                <BackLink
                    href={back}
                    aria-label="Back"
                    className="text-muted-foreground hover:text-foreground relative flex shrink-0 items-center pt-0.5 pointer-coarse:after:absolute pointer-coarse:after:-inset-[14px] pointer-coarse:after:content-[''] [&_svg]:size-4"
                >
                    <ArrowLeft />
                </BackLink>
            ) : null}
            <div
                className={cn(
                    'flex min-w-0 flex-auto flex-col gap-1 md:flex-none',
                    // A subpage's title takes the whole row on a phone, so the actions wrap
                    // underneath it rather than squeezing the record's name into two lines or an
                    // ellipsis. A record you cannot read the name of is not a record.
                    subpage && 'max-md:basis-full',
                )}
            >
                {/* The phone's top bar already names the section, so the eyebrow would say it twice there. */}
                {!subpage && eyebrow ? (
                    <span className="text-label-sm text-muted-foreground hidden lg:block">
                        {eyebrow}
                    </span>
                ) : null}
                {subpage && breadcrumb ? (
                    <span className="text-label-sm text-muted-foreground">{breadcrumb}</span>
                ) : null}
                <h1 className="text-page-title text-foreground">{title}</h1>
                {subpage && subtitle ? (
                    <p className="text-muted-foreground text-sm">{subtitle}</p>
                ) : null}
            </div>
            {!subpage && search ? (
                <div className="relative order-3 w-full flex-none md:order-none md:w-[360px] md:max-w-[40%]">
                    {search}
                </div>
            ) : null}
            {actions || actor ? (
                // On a phone a subpage's actions take a row of their own under the title, so a long
                // record name is not squeezed into a column beside them. They start on the gutter,
                // where the title and breadcrumb do, not indented past the Back arrow (ALS-307).
                <div
                    className={cn(
                        'order-2 flex flex-wrap items-center gap-4 md:order-none md:ml-auto md:justify-end',
                        subpage && 'max-md:basis-full',
                    )}
                >
                    {actions}
                    {subpage && actor ? (
                        <span className="border-border bg-card inline-flex items-center gap-2 rounded-full border py-1 pr-3 pl-1">
                            <Avatar size="sm" tone="light">
                                <AvatarFallback>
                                    {actor.name.slice(0, 2).toUpperCase()}
                                </AvatarFallback>
                            </Avatar>
                            <span className="text-foreground text-sm font-medium">
                                {actor.name}
                            </span>
                            {actor.state ? (
                                <span className="text-label-sm text-muted-foreground">
                                    {actor.state}
                                </span>
                            ) : null}
                        </span>
                    ) : null}
                </div>
            ) : null}
        </header>
    );
}

export { PageHeader, type PageHeaderProps };
